import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  buildWizardStepDependencyMeta,
  DEFAULT_MAX_RESULT_COUNT,
  DEFAULT_TOASTER_LIFE,
  getWizardStepData,
  ISummaryPillItem,
  IWizardContext,
  persistWizardStepData,
  SelectOption,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnButton,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import {
  SelectValue,
  toSingleSelectValue,
} from '../contract-assignment-by-stand.shared';
import { ContractAssociation } from '../services/contract-association';
import { tap } from 'rxjs';
import { RegisterService } from 'cbd-proxies';
import { IAssociatePropertyStepData } from '../interfaces/IAssociatePropertyStepData';
import { formatDateOnlyFieldValue } from 'projects/shared/src/lib/utils/date-only-value';
import { IRegisterRow } from '../interfaces/IRegisterRow';

@Component({
  selector: 'lib-step-associate-property',
  standalone: true,
  imports: [LocalizationPipe, ZnButton, ZnSingleSelect],
  templateUrl: './step-associate-property.html',
})
export class StepAssociateProperty implements WizardStepComponent<IAssociatePropertyStepData> {
  private readonly toaster = inject(ToasterService);
  private readonly contractAssociationService = inject(ContractAssociation);
  private readonly registerService = inject(RegisterService);

  readonly propertySelect = viewChild('propertySelect', { read: ZnSingleSelect });

  readonly localizationKeyPrefix =
    'Geographic::ContractAssignmentByStand.StepAssociateProperty';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly columnHeaderKeys = {
    name: 'LanguageService::Number',
    department: 'Planning::Entity.Register.Department',
    judicialSection: `${this.localizationKeyPrefix}.Register.Columns.JudicialSection`,
    documentaryArea: 'Planning::Entity.Register.DocumentaryArea',
    coneatIndex: 'Planning::Entity.Register.ConeatIndex',
    deedValue: 'Planning::Entity.Register.DeedValue',
    fiscalValue: 'Planning::Entity.Register.FiscalValue',
    previousName: 'Planning::Entity.Register.PreviousName',
    description: 'Planning::Entity.Register.Description',
    propertyType: 'Controlling::Entity.PropertyTypeRegionParameter.PropertyType',
    foManagement: 'Planning::Entity.PropertyType.IsFOManagement',
    lastManagementStart: `${this.localizationKeyPrefix}.Register.Columns.LastManagementStart`,
  };

  readonly propertyOptions = signal<SelectOption<string>[]>([]);

  readonly selectedProperty = signal<string | null>(null);
  readonly rows = signal<IRegisterRow[]>([]);

  private readonly properties = new Map<string, string>();
  private stepData: IAssociatePropertyStepData = {} as IAssociatePropertyStepData;

  loadProperties = (params?: ZnSelectQueryParams) => this.contractAssociationService.loadProperties({
      ...params,
    }).pipe(
      tap(response => {
        response.items.forEach(item => {
          this.properties.set(item.key, item.value);
        });
      }),
    );

  readonly propertySectionClass = computed(() =>
    this.selectedProperty()
      ? 'zn-step-content-section'
      : 'zn-step-content-section zn-step-content-section--selected',
  );

  readonly registerSectionClass = computed(() => {
    if (!this.selectedProperty()) {
      return 'zn-full-height zn-step-content-section zn-step-content-section--disabled';
    }

    return 'zn-full-height zn-step-content-section zn-step-content-section--selected';
  });

  onPropertyChange(value: SelectValue): void {
    this.rows.set([]);

    this.selectedProperty.set(toSingleSelectValue(value));
    this.loadRegisters();
  }

  reloadRegisters(): void {
    if (!this.selectedProperty()) {
      this.notifyMissingProperty();
      return;
    }

    this.loadRegisters();
  }

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });
    const previousData: IAssociatePropertyStepData | undefined = getWizardStepData(context, context.activeStepId());

    if(previousData) this.loadPreviousData(previousData, context);
  }

  canLeave(): boolean {
    if (this.selectedProperty()) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::ContractAssignmentByStand.StepAssociateProperty.cantContinue`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): IAssociatePropertyStepData {
    const propertyId = this.selectedProperty() as string;
    const propertyName =
      this.properties.get(propertyId) ?? this.stepData.propertyName ?? null;

    this.stepData = {
      propertyId: propertyId,
      propertyName,
      RegisterRows: this.rows()
    }

    persistWizardStepData(context, {
      valid: !!this.selectedProperty(),
      visited: true,
      completed: true,
      data: this.stepData,
      dirty: true,
      meta: buildWizardStepDependencyMeta(this.stepData),
      invalidateStepIds: [3, 4],
    });
    return this.stepData;
  }

  private loadRegisters(): void {
    const propertyId = this.selectedProperty();

    if (!propertyId) {
      this.rows.set([]);
      return;
    }

    this.registerService.getList({
      maxResultCount: DEFAULT_MAX_RESULT_COUNT,
      propertyId: [Number(propertyId)]
    }).subscribe({
      next: result => {
        const rows: any[] = (result.items ?? []).map(item => {
          return {
            coneatIndex: item.coneatIndex,
            deedValue: item.deedValue,
            department: item.department?.name ?? item.departmentId?.toString(),
            description: item.description,
            documentaryArea: item.documentaryArea,
            fiscalValue: item.fiscalValue,
            foManagement: item.propertyType?.isFOManagement,
            id: item.id,
            judicialSection: item.judicialCadastralSection?.name ?? item.judicialCadastralSectionId?.toString(),
            lastManagementStart: formatDateOnlyFieldValue(item.purchaseDate as string),
            name: item.name,
            previousName: item.previousName,
            propertyType: item.propertyType?.name ?? item.propertyTypeId?.toString()
          };
        });

        this.rows.set(rows);
      },
      error: error => {
        console.log(error)
      }
    });
  }

  buildSummary(data: IAssociatePropertyStepData): ISummaryPillItem[] {
    return data.propertyName
      ? [{ label: 'Planning::Entity.Property', value: data.propertyName }]
      : [];
  }

  private notifyMissingProperty(): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::ContractAssignmentByStand.StepAssociateProperty.cantContinue`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }

  private loadPreviousData(data: IAssociatePropertyStepData, context: IWizardContext): void {
    this.stepData = data;
    this.selectedProperty.set(data.propertyId)

    if (data.propertyId && data.propertyName) {
      this.properties.set(data.propertyId, data.propertyName);
    }

    this.propertyOptions.set(
      data.propertyId && data.propertyName
        ? [{ key: data.propertyId, value: data.propertyName }]
        : [],
    );
    this.rows.set(data.RegisterRows);

    queueMicrotask(() => {
      this.propertySelect()?.writeValue(data.propertyId);
    });

    context.patchStepState(context.activeStepId(), {
      visited: true,
      dirty: true,
      completed: true,
      data: data
    });
  }
}
